import { forwardRef, useCallback, useEffect, useRef, useState, ForwardedRef, useMemo, FormEvent } from 'react';
import classNames from 'classnames';
import { IconButton } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import LinkIcon from '@mui/icons-material/Link';

import { cleanHtml, exec, getCommonAncestorElement } from './util';
import { Editor, Root, Toolbar } from './styled';

export default forwardRef(function ContentEditor(
    {
        defaultValue,
        onChange,
        onBlur,
        placeholder,
        isLoading,
    }: {
        defaultValue: string;
        onChange?: (value: string) => void;
        onBlur?: () => void;
        placeholder?: string;
        isLoading?: boolean;
    },
    ref: ForwardedRef<HTMLDivElement>,
) {
    const editorRef = useRef<HTMLDivElement>(null);
    const [toolbarPosition, setToolbarPosition] = useState<{ top: number; left: number } | null>(null);
    const [isEmpty, setIsEmpty] = useState(false);

    const actions = useMemo(
        () => [
            { icon: FormatBoldIcon, command: 'bold' },
            { icon: FormatItalicIcon, command: 'italic' },
            { icon: FormatUnderlinedIcon, command: 'underline' },
            { icon: FormatListBulletedIcon, command: 'insertUnorderedList' },
            { icon: FormatListNumberedIcon, command: 'insertOrderedList' },
            {
                icon: LinkIcon,
                command: () => {
                    const range = window.getSelection()?.getRangeAt(0);
                    if (!range) return;
                    if (getCommonAncestorElement(range)?.tagName === 'A') {
                        exec('unlink');
                    } else {
                        const url = window.prompt('Enter URL');
                        if (url) {
                            exec('createLink', url);
                        }
                    }
                },
            },
            {
                icon: FormatClearIcon,
                command: () => {
                    exec('unlink');
                    exec('removeFormat');
                },
            },
        ],
        [],
    );

    useEffect(() => {
        if (editorRef.current) {
            const clean = cleanHtml(defaultValue || '');
            setIsEmpty(clean === '');
            editorRef.current.innerHTML = clean;

            editorRef.current.querySelectorAll('div').forEach((div) => {
                div.contentEditable = 'false';
            });
        }
    }, [defaultValue, editorRef.current]);

    useEffect(() => {
        const handleSelectionChange = () => {
            if (!editorRef.current) return;
            const sel = window.getSelection();
            if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
                setToolbarPosition(null);
                return;
            }
            const range = sel.getRangeAt(0);

            if (!editorRef.current.contains(range.commonAncestorContainer)) {
                setToolbarPosition(null);
                return;
            }

            const rect = range.getBoundingClientRect();
            const editorRect = editorRef.current.getBoundingClientRect();

            if (rect.width === 0 && rect.height === 0) {
                setToolbarPosition(null);
                return;
            }
            setToolbarPosition({
                top: rect.top - editorRect.top + rect.height,
                left: rect.left - editorRect.left,
            });
        };

        document.addEventListener('selectionchange', handleSelectionChange);
        return () => document.removeEventListener('selectionchange', handleSelectionChange);
    }, []);

    const onBeforeInput = useCallback(
        (event: FormEvent<HTMLDivElement>) => {
            const selection = window.getSelection();
            if (selection?.rangeCount) {
                const range = selection!.getRangeAt(0);

                if (getCommonAncestorElement(range).tagName === 'LI') {
                    return;
                }
            }

            if (event.nativeEvent.type === 'textInput' && (event.nativeEvent as TextEvent).data === '\n') {
                event.preventDefault();
                exec('insertLineBreak');
            }
        },
        [exec],
    );

    const onInput = useCallback(
        (event: FormEvent<HTMLDivElement>) => {
            const selection = window.getSelection();
            const range = selection!.getRangeAt(0);
            if ((event.nativeEvent as InputEvent).inputType === 'insertParagraph') {
                const node = range.commonAncestorContainer as HTMLElement;
                if (node.tagName === 'DIV' && node.innerText === '\n') {
                    const newNode = document.createTextNode('\n');
                    range.deleteContents();
                    node.replaceWith(newNode);
                }
            }
            const clean = cleanHtml((event.target as HTMLDivElement).innerHTML);
            onChange?.(clean);
            setIsEmpty(clean === '');
        },
        [onChange],
    );

    return (
        <Root ref={ref}>
            <Editor
                ref={editorRef}
                contentEditable={!isLoading && !!onChange}
                onBlur={onBlur}
                suppressContentEditableWarning
                data-placeholder={placeholder}
                className={classNames({
                    empty: !isLoading && isEmpty,
                    loading: isLoading,
                })}
                onBeforeInput={onBeforeInput}
                onInput={onInput}
                style={{
                    whiteSpace: 'pre-wrap',
                    caretColor: '#65E2AE',
                }}
            />

            {toolbarPosition && (
                <Toolbar
                    style={{
                        top: `${toolbarPosition.top}px`,
                        left: `${toolbarPosition.left}px`,
                    }}
                    onMouseDown={(event) => event.preventDefault()}
                >
                    {actions.map((action, index) => (
                        <IconButton
                            key={index}
                            size="small"
                            onMouseDown={(event) => {
                                event.preventDefault();
                                typeof action.command === 'function' ? action.command() : exec(action.command);
                            }}
                        >
                            <action.icon fontSize="small" />
                        </IconButton>
                    ))}
                </Toolbar>
            )}
        </Root>
    );
});
